<script setup>
import {gameScoreText, scorePoints} from '~/utils/gameScore';
import { computed } from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {useTimelineModels} from "@/composables/useTimelineModels";
import {useAuthService} from "@/composables/useAuthService";
import {useValidation} from "@/composables/useValidation";
import {useVisitorPreview} from "@/composables/useVisitorPreview";
import AvatarImageLoader from "@/components/loaders/AvatarImageLoader.vue";

// Compact result row (Flutter result-row shape — fixture-list / recent results):
// two stacked team rows (winner bold, loser subtle) with per-game scores inline
// and the set result on the right, plus a competition · date meta line.
const props = defineProps({
  fixture: { type: Object, default: () => ({}) },
  // live mode: the fixture is being scored right now — LIVE chip, link to the live
  // page, and no result actions (the score comes pre-shaped from the LiveNow feed)
  live: { type: Boolean, default: false },
});
const emit = defineEmits(['action']);

const route = useRoute();
const router = useRouter();
const {user, isAuthenticated, isSuperUser, isAdminOfClub} = useAuthService();
const {isPlayerInFixture, fixtureResultAction, fixtureResultActionLabel, fixtureResultActionIcon} = useValidation();
// X73: in a club page's visitor preview the row is a visitor's: no Record / Approve / Edit result.
const visitorPreview = useVisitorPreview();
const signedIn = computed(() => isAuthenticated.value && !visitorPreview.value);

const {teamName, teamInitials, teamAvatar} = useTimelineModels();

const isFixtureAdmin = computed(() =>
  signedIn.value && (isSuperUser.value || isAdminOfClub(props.fixture?.clubId)));

// Correct action for this user (Record / Approve / Edit result). Shown only to the
// participants or an admin so public recent-results rows stay read-only.
const resultAction = computed(() =>
  fixtureResultAction(props.fixture, {playerId: user.value?.playerId, isAdmin: isFixtureAdmin.value}));

const canAct = computed(() =>
  isFixtureAdmin.value || (signedIn.value && isPlayerInFixture(user.value?.playerId, props.fixture)));

// Both sides must be assigned — you can't record/approve a result for a TBC slot
// (e.g. an unfilled knockout fixture or a bye).
const hasBothTeams = computed(() =>
  props.fixture?.homeTeamId != null && props.fixture?.awayTeamId != null);

const showAction = computed(() => !props.live && canAct.value && hasBothTeams.value && resultAction.value != null);

// Open the score dialog directly (over the current page via editingFixtureId, consumed by
// BasePage) and tell the parent to close the approvals dialog so it isn't stacked behind.
const openAction = () => {
  emit('action', props.fixture);
  router.push({path: route.path, query: {...route.query, editingFixtureId: props.fixture.id}});
};

const homeName = computed(() => teamName(props.fixture?.homeTeam));
const awayName = computed(() => teamName(props.fixture?.awayTeam));

const homeImage = computed(() => teamAvatar(props.fixture?.homeTeam));
const awayImage = computed(() => teamAvatar(props.fixture?.awayTeam));

const isPlayed = computed(() =>
  props.fixture?.homeTeamResult != null && props.fixture?.awayTeamResult != null,
);

// The winner is ALWAYS decided by the points awarded, never the result text.
const homePts = computed(() => props.fixture?.homeTeamPoints);
const awayPts = computed(() => props.fixture?.awayTeamPoints);
const homeWon = computed(() => isPlayed.value && homePts.value != null && awayPts.value != null && homePts.value > awayPts.value);
const awayWon = computed(() => isPlayed.value && homePts.value != null && awayPts.value != null && awayPts.value > homePts.value);

// per-game scores in play order
const games = computed(() =>
  (props.fixture?.games || []).slice().sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)),
);

const { outputDateFromDb } = useCrudConverters();
const date = computed(() => {
  const d = props.fixture?.playedAt || props.fixture?.scheduledAt;
  return d ? outputDateFromDb(d, 'd MMM') : null;
});

// Competition context + format icon/colour (Flutter FixtureResultLines meta line):
// league = red table, ladder = blue ladder, tournament = amber trophy,
// americano = teal account-switch (linked to its cycle),
// meet = green handshake (linked to its event), else grey friendly.
const format = computed(() => {
  const f = props.fixture;
  if (f?.leagueId || f?.leagueName || f?.leagueCycleId) {
    const name = [f.leagueName, f.leagueCycleDivisionName].filter(Boolean).join(' · ');
    return {name, icon: 'mdi-table', color: 'primary'};
  }
  if (f?.ladderId || f?.ladderName) return {name: f.ladderName, icon: 'mdi-ladder', color: '#2477C8'};
  if (f?.tournamentId || f?.tournamentName) {
    const name = [f.tournamentStageName, f.tournamentName].filter(Boolean).join(' · ');
    return {name, icon: 'mdi-trophy', color: 'warning'};
  }
  if (f?.americanoCycleDivisionId || f?.americanoId) {
    const name = [f.americanoName || 'Americano league', f.americanoRound ? `Round ${f.americanoRound}` : null]
        .filter(Boolean).join(' · ');
    return {name, icon: 'mdi-account-switch', color: '#7C3AED'};
  }
  if (f?.meetOccurrenceId) {
    return {name: f.meetName || 'Meet', icon: 'mdi-handshake', color: 'success'};
  }
  return {name: 'Friendly', icon: 'mdi-handshake-outline', color: 'textSubtle'};
});

// Meet matches deep-link their footer meta to the event page; americano
// matches to their league's cycle.
const meetRoute = computed(() => {
  if (props.fixture?.meetOccurrenceId) {
    return {name: 'MeetOccurrenceDetail', params: {meetId: props.fixture.meetId, occurrenceId: props.fixture.meetOccurrenceId}};
  }
  if (props.fixture?.americanoCycleId && props.fixture?.americanoId) {
    return {name: 'AmericanoDetail', params: {americanoId: props.fixture.americanoId, cycleId: props.fixture.americanoCycleId}};
  }
  return null;
});

const iconColor = computed(() => {
  const c = format.value.color;
  return c.startsWith('#') ? c : `rgb(var(--v-theme-${c}))`;
});

// Area only shown while unplayed - once there's a result, where it happened is noise
const metaLine = computed(() => [
  format.value.name,
  date.value,
  !isPlayed.value ? props.fixture?.playingAreaName : null,
].filter(Boolean).join(' · '));

// Each game column sizes to the wider of its two scores so home and away cells line
// up exactly (tabular digits make ch-widths true); style is otherwise untouched.
// (calc includes the cell's 8px left padding: the app's global border-box sizing
// would otherwise swallow the ch width into the padding)
const gameColWidth = (g) =>
  `calc(${Math.max(scorePoints(g.homeTeamScore).length, scorePoints(g.awayTeamScore).length)}ch + 8px)`;

// Per-game cell class: winner green, loser brand, draw neutral (Flutter).
// In-play units (live rows) stay neutral — they aren't decided yet.
const gameClass = (mine, theirs) => mine === theirs ? 'fxr-g--draw' : (mine > theirs ? 'fxr-g--w' : 'fxr-g--l');
</script>

<template>
  <router-link class="fxr tl-card" :to="live ? `/fixtures/${fixture.id}/live` : `/fixtures/${fixture.id}`">
    <!-- Home team row -->
    <div class="fxr-team" :class="{ 'fxr-team--lost': awayWon }">
      <div class="fxr-id">
        <avatar-image-loader
            v-if="homeImage"
            :image-file-name="homeImage"
            :image-clickable="false"
            size="22"
            class="fxr-face"
        />
        <span v-else class="fxr-face fxr-face--initials">{{ teamInitials(fixture.homeTeam) }}</span>
        <span class="fxr-name">{{ homeName }}</span>
      </div>
      <div class="fxr-games" v-if="isPlayed && games.length">
        <span
            v-for="g in games"
            :key="g.id + '-h'"
            class="fxr-g"
            :class="g.inPlay ? 'fxr-g--draw' : gameClass(g.homeTeamScore, g.awayTeamScore)"
            :style="{ minWidth: gameColWidth(g) }"
        >{{ scorePoints(g.homeTeamScore) }}</span>
      </div>
      <span class="fxr-sep" v-if="isPlayed && games.length"></span>
      <span class="fxr-res" v-if="isPlayed">{{ fixture.homeTeamResult }}</span>
    </div>

    <!-- Away team row -->
    <div class="fxr-team" :class="{ 'fxr-team--lost': homeWon }">
      <div class="fxr-id">
        <avatar-image-loader
            v-if="awayImage"
            :image-file-name="awayImage"
            :image-clickable="false"
            size="22"
            class="fxr-face"
        />
        <span v-else class="fxr-face fxr-face--initials">{{ teamInitials(fixture.awayTeam) }}</span>
        <span class="fxr-name">{{ awayName }}</span>
      </div>
      <div class="fxr-games" v-if="isPlayed && games.length">
        <span
            v-for="g in games"
            :key="g.id + '-a'"
            class="fxr-g"
            :class="g.inPlay ? 'fxr-g--draw' : gameClass(g.awayTeamScore, g.homeTeamScore)"
            :style="{ minWidth: gameColWidth(g) }"
        >{{ scorePoints(g.awayTeamScore) }}</span>
      </div>
      <span class="fxr-sep" v-if="isPlayed && games.length"></span>
      <span class="fxr-res" v-if="isPlayed">{{ fixture.awayTeamResult }}</span>
    </div>

    <!-- goal scorers, denormalised on the fixture ("Smith 12', 55'") -->
    <div v-if="isPlayed && (fixture.homeScorersSummary || fixture.awayScorersSummary)" class="fxr-scorers">
      <template v-for="side in ['home', 'away']" :key="side">
        <span
            v-for="(entry, i) in ((side === 'home' ? fixture.homeScorersSummary : fixture.awayScorersSummary) ?? '')
                .split(' · ').map(e => e.trim()).filter(Boolean)"
            :key="side + i" class="fxr-scorer-chip">
          <v-icon size="10" icon="mdi-soccer"/>{{ entry }}
        </span>
      </template>
    </div>

    <div class="fxr-foot" v-if="metaLine || showAction || live">
      <span v-if="live" class="fxr-live"><span class="fxr-live-dot"/>LIVE</span>
      <!-- meet matches: the footer meta links straight to the event -->
      <router-link v-if="metaLine && meetRoute" :to="meetRoute" class="fxr-meta" @click.stop>
        <v-icon :icon="format.icon" size="13" :style="{ color: iconColor }" class="fxr-meta-icon"/>
        <span class="fxr-meta-text">{{ metaLine }}</span>
        <v-tooltip activator="parent" location="top">{{ metaLine }}</v-tooltip>
      </router-link>
      <div class="fxr-meta" v-else-if="metaLine">
        <v-icon :icon="format.icon" size="13" :style="{ color: iconColor }" class="fxr-meta-icon"/>
        <span class="fxr-meta-text">{{ metaLine }}</span>
        <v-tooltip activator="parent" location="top">{{ metaLine }}</v-tooltip>
      </div>
      <v-spacer/>
      <!-- compact rows: icon-only live scoring next to the result action -->
      <v-btn v-if="showAction && !isPlayed && fixture.supportsLiveScoring"
             size="small" variant="outlined" rounded="pill" class="text-none flex-shrink-0 mr-1"
             icon="mdi-scoreboard-outline"
             @click.stop.prevent="$router.push(`/fixtures/${fixture.id}/live`)">
        <v-icon size="16">mdi-scoreboard-outline</v-icon>
        <v-tooltip activator="parent" location="top">Live score</v-tooltip>
      </v-btn>
      <v-btn v-if="showAction" size="small" variant="flat" color="primary" rounded="pill"
             class="text-none flex-shrink-0"
             :prepend-icon="fixtureResultActionIcon(resultAction)"
             @click.stop.prevent="openAction">{{ fixtureResultActionLabel(resultAction) }}</v-btn>
    </div>
  </router-link>
</template>

<style scoped lang="scss">
.fxr {
  display: flex;
  flex-direction: column;
  padding: 14px 16px;
  margin-bottom: 10px;
  text-decoration: none;
  color: rgb(var(--v-theme-text));
  transition: border-color 0.15s ease;

  &:hover {
    border-color: rgb(var(--v-theme-textSubtle));
  }
}

/* one team row */
.fxr-team {
  display: flex;
  align-items: center;
  gap: 10px;

  & + .fxr-team {
    margin-top: 7px;
  }

  /* loser: subtle ink + lighter weight (mockup .row2.lost) */
  &.fxr-team--lost {
    .fxr-name,
    .fxr-res {
      color: rgb(var(--v-theme-textSubtle));
      font-weight: 500;
    }
  }
}

.fxr-id {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.fxr-face {
  width: 22px;
  height: 22px;
  border-radius: 50% !important;
  flex-shrink: 0;
}

.fxr-face--initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
  font-size: 9px;
  font-weight: 700;
}

.fxr-name {
  font-size: 13.5px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* per-game scores — intrinsic width (Flutter FixtureResultLines) so multi-digit
   scores never overlap or wrap; a left gap separates the columns. */
.fxr-games {
  display: flex;
  flex-shrink: 0;
}

.fxr-g {
  min-width: 14px;
  padding-left: 8px;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  &.fxr-g--w {
    color: rgb(var(--v-theme-success));
  }
  &.fxr-g--l {
    color: rgb(var(--v-theme-primary));
  }
  &.fxr-g--draw {
    color: rgb(var(--v-theme-textSubtle));
  }
}

/* tight separator between the game scores and the final result */
.fxr-sep {
  width: 1px;
  height: 14px;
  background: rgb(var(--v-theme-borderLight));
  margin: 0 2px;
  flex-shrink: 0;
}

.fxr-res {
  min-width: 14px;
  text-align: right;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
  white-space: nowrap;
}

.fxr-foot {
  display: flex;
  align-items: center;
  gap: 10px;
  /* auto margin pins the footer to the card's bottom when a fixed height is
     imposed (e.g. bracket views); padding keeps the 10px gap at natural height */
  margin-top: auto;
  padding-top: 10px;
}

/* live mode: the row's one red */
.fxr-live {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  background: rgb(var(--v-theme-primary));
  color: #fff;
  border-radius: 999px;
  padding: 3px 9px;
  flex-shrink: 0;
}

.fxr-live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #fff;
  animation: fxr-live-pulse 1.6s ease-in-out infinite;
}

@keyframes fxr-live-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}

@media (prefers-reduced-motion: reduce) {
  .fxr-live-dot { animation: none; }
}

.fxr-meta {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-textSubtle));
  text-decoration: none; /* meet matches render this as a link to their event */
  min-width: 0;
}

.fxr-meta-icon {
  flex-shrink: 0;
}

.fxr-meta-text {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* scorer chips: plain background, subtle border, breathing room from the
   dashed divider — a run-on text line was unreadable past a couple of goals */
.fxr-scorers {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding-top: 8px;
  border-top: 1px dashed rgb(var(--v-theme-borderLight));
  margin-top: 8px;
}

.fxr-scorer-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10.5px;
  line-height: 1;
  color: rgb(var(--v-theme-textSubtle));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 999px;
  padding: 3px 7px;
  white-space: nowrap;
}
</style>
